import React, { useState } from 'react';
import { Modal, Button } from '../common';
import client from '../../api/client';

export const PdfExportModal = ({ isOpen, onClose, gameId }) => {
  const [options, setOptions] = useState({
    includePrompts: true,
    includeWinningDetails: true,
    includeAspects: true,
    includeAttribution: true,
    includeCharacters: true,
    includePremise: true,
  });
  const [isExporting, setIsExporting] = useState(false);

  const handleOptionChange = (key) => {
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Build query params from options
      const params = new URLSearchParams();
      if (!options.includePrompts) params.append('includePrompts', 'no');
      if (!options.includeWinningDetails) params.append('includeWinningDetails', 'no');
      if (!options.includeAspects) params.append('includeAspects', 'no');
      if (!options.includeAttribution) params.append('includeAttribution', 'no');
      if (!options.includeCharacters) params.append('includeCharacters', 'no');
      if (!options.includePremise) params.append('includePremise', 'no');

      const queryString = params.toString();
      const url = `/api/plugins/pdf-export/games/${gameId}${queryString ? `?${queryString}` : ''}`;

      const response = await client.get(url, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      link.download = filenameMatch ? filenameMatch[1] : 'story.pdf';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      onClose();
    } catch (err) {
      console.error('Failed to export PDF:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const checkboxOptions = [
    { key: 'includePrompts', label: 'GM Prompts', description: 'Include the GM prompt text for each round' },
    { key: 'includeWinningDetails', label: 'Winning Details', description: 'Include winning detail proposals' },
    { key: 'includeAspects', label: 'Aspect Badges', description: 'Show referenced aspects on content blocks' },
    { key: 'includeAttribution', label: 'Player Attribution', description: 'Show which player contributed each block' },
    { key: 'includeCharacters', label: 'Characters Section', description: 'Include the characters page' },
    { key: 'includePremise', label: 'Premise Section', description: 'Include the setting, situation, and tone' },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export PDF" size="md">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Choose what to include in your PDF export:
        </p>

        <div className="space-y-3">
          {checkboxOptions.map(({ key, label, description }) => (
            <label
              key={key}
              className="flex items-start gap-3 cursor-pointer select-none"
            >
              <input
                type="checkbox"
                checked={options[key]}
                onChange={() => handleOptionChange(key)}
                className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">{label}</span>
                <p className="text-xs text-gray-500">{description}</p>
              </div>
            </label>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="secondary" onClick={onClose} disabled={isExporting}>
            Cancel
          </Button>
          <Button onClick={handleExport} loading={isExporting}>
            {isExporting ? 'Exporting...' : 'Export PDF'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
